import Cookies from "universal-cookie"

import { config } from "./config"
import { waitFor } from "xstate"
import { authMachine } from "../machines/AuthMachine"

export const cookies = new Cookies()

export async function fetchAPI<T, R extends "json" | "blob" | "raw" = "json">(
  url: string,
  init: Omit<RequestInit, "body"> & {
    body?: Record<string, unknown> | RequestInit["body"]
  } = {},
  returnAs?: R,
) {
  let retries = 0

  while (true) {
    await waitFor(authMachine, (snapshot) => snapshot.hasTag("Settled"))

    const token = authMachine.getSnapshot().context.token

    if (token == null) {
      authMachine.send({ type: "EXTERNAL_LOGIN" })
      continue
    }

    const headers = { ...init.headers, Authorization: `Bearer ${token.access_token}` } as unknown as Record<
      string,
      string
    >
    if (typeof init.body === "object") headers["Content-Type"] = "application/json"

    const res = await fetch(config.apiUrl + url, {
      ...init,
      headers,
      body: typeof init.body === "object" ? JSON.stringify(init.body) : init.body,
    })

    let errors: APIError[] = [
      {
        errorMessage: "Unknown error",
        propertyName: "",
        attemptedValue: "",
        customState: null,
        severity: "Error",
        errorCode: "Unknown",
        formattedMessagePlaceholderValues: {},
      },
    ]

    if (res.status === 401 && ++retries <= 1) {
      authMachine.send({ type: "REFRESH_TOKEN" })
      continue
    } else if (!res.ok) {
      try {
        errors = (await res.json()) as APIError[]
      } catch (error) {}
    }

    return {
      errors,
      ok: res.ok,
      status: res.status,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      result: res.ok ? (returnAs === "raw" ? res : await (returnAs === "blob" ? res.blob() : res.json())) : undefined,
    } as RouteResponse<R extends "blob" ? Blob : R extends "raw" ? Response : T>
  }
}

export type RouteResponse<T> = RouteData<T> | RouteError

interface RouteData<T> {
  ok: true
  result: T
  status: number
  errors?: never
}

interface RouteError {
  ok: false
  status: number
  errors: APIError[]
  result?: never
}

export interface APIError {
  propertyName: string
  errorMessage: string
  attemptedValue: string
  customState: null
  severity: string
  errorCode: string
  formattedMessagePlaceholderValues: Record<string, string | number>
}
